/* eslint-disable react/prop-types */
import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";

function DeleteModal({
  setDeleteIsModalOpen,
  handleDeleteItem,
  isDeleteModalOpen,
  itemType,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const handleClose = () => setDeleteIsModalOpen(false);

  return (
    <Dialog
      open={isDeleteModalOpen}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.checklist.surface.subtle,
          borderBottom: 1,
          borderColor: theme.palette.checklist.border.subtle,
          pb: 2,
          pt: 3,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "error.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "error.contrastText",
            }}
          >
            <DeleteForeverOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {t("deleteModal.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("deleteModal.subtitle")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              bgcolor: theme.palette.checklist.interactive.hover,
              "&:hover": {
                bgcolor: theme.palette.checklist.interactive.selected,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3, bgcolor: theme.palette.checklist.surface.elevated }}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t("deleteModal.confirmMessage", { itemType })}
        </Typography>
        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.checklist.surface.subtle,
            borderRadius: 1,
            border: 1,
            borderColor: "error.light",
          }}
        >
          <Typography
            variant="body2"
            color="error.main"
            sx={{ fontWeight: "medium" }}
          >
            ⚠️ {t("deleteModal.warning")}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          bgcolor: theme.palette.checklist.surface.elevated,
          borderTop: 1,
          borderColor: theme.palette.checklist.border.subtle,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "medium",
          }}
        >
          {t("deleteModal.cancelButton")}
        </Button>
        <Button
          onClick={handleDeleteItem}
          variant="contained"
          color="error"
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "bold",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-1px)",
            },
          }}
        >
          {t("deleteModal.deleteButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default React.memo(DeleteModal);
